import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "bg-[#14171A] border-[#A8AEB2]/25 text-white placeholder:text-[#A8AEB2]/60 focus-visible:ring-[#7AB648] focus-visible:ring-offset-0 h-12 rounded-sm";

export default function Bookings() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setDone(true);
      toast.success("Booking request sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call us on 07984 004171.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="bookings"
      className="relative z-10 bg-[#14171A] py-20 md:py-32 border-t border-[#A8AEB2]/15"
      data-testid="bookings-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left copy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="font-body text-sm uppercase tracking-[0.4em] text-[#7AB648]">
            Book a Job
          </span>
          <h2 className="font-display uppercase text-5xl md:text-7xl text-white leading-[0.9] mt-4">
            Request a<br />
            <span style={{ WebkitTextStroke: "1.5px #7AB648", color: "transparent" }}>
              Quote
            </span>
          </h2>
          <p className="mt-6 font-body text-base md:text-lg text-[#A8AEB2] max-w-md leading-relaxed">
            Tell us what you need and when. We&apos;ll get back to you with a free,
            no-obligation quote — usually within one working day.
          </p>
          <div className="mt-10 space-y-4">
            {["Free site visit & quote", "No obligation", "Fully insured team"].map(
              (item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#7AB648]" />
                  <span className="font-body text-white">{item}</span>
                </div>
              )
            )}
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            className="rounded-sm border border-[#A8AEB2]/15 bg-[#1E2227] p-6 md:p-10"
            data-testid="booking-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-white font-body">Full Name *</Label>
                <Input
                  className={inputClass}
                  placeholder="John Smith"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="booking-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white font-body">Phone *</Label>
                <Input
                  className={inputClass}
                  placeholder="07400 123456"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="booking-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white font-body">Email *</Label>
                <Input
                  type="email"
                  className={inputClass}
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="booking-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white font-body">Service *</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                >
                  <SelectTrigger
                    className={inputClass}
                    data-testid="booking-service-select"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E2227] border-[#A8AEB2]/25 text-white max-h-72">
                    {SERVICE_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt}
                        value={opt}
                        className="text-white focus:bg-[#7AB648] focus:text-[#14171A] cursor-pointer"
                        data-testid={`booking-service-option-${opt.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                      >
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white font-body">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-3 px-3 h-12 rounded-sm border border-[#A8AEB2]/25 bg-[#14171A] text-left font-body transition-colors hover:border-[#7AB648] ${
                        date ? "text-white" : "text-[#A8AEB2]/60"
                      }`}
                      data-testid="booking-date-trigger"
                    >
                      <CalendarIcon size={18} className="text-[#7AB648]" />
                      {date ? format(date, "PPP") : "Pick a preferred date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-[#1E2227] border-[#A8AEB2]/25"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white font-body">Details</Label>
                <Textarea
                  className="bg-[#14171A] border-[#A8AEB2]/25 text-white placeholder:text-[#A8AEB2]/60 focus-visible:ring-[#7AB648] focus-visible:ring-offset-0 rounded-sm min-h-[120px]"
                  placeholder="Tell us a bit about the job..."
                  value={form.message}
                  onChange={update("message")}
                  data-testid="booking-message-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-8 inline-flex w-full md:w-auto items-center justify-center gap-2 rounded-sm bg-[#7AB648] px-10 py-4 font-head font-bold uppercase tracking-wide text-[#14171A] transition-colors hover:bg-[#6BA339] active:scale-95 disabled:opacity-60"
              data-testid="booking-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : done ? (
                <>
                  <CheckCircle2 size={18} /> Sent — Send Another
                </>
              ) : (
                "Send Request"
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
